import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircleDollarSign, Sparkles, Wallet, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

const DEFAULT_SLOTS = [
  { id: "miss-left", label: "0x", multiplier: 0, color: "from-slate-500 to-slate-700" },
  { id: "one-left", label: "1x", multiplier: 1, color: "from-sky-500 to-blue-700" },
  { id: "two-left", label: "2x", multiplier: 2, color: "from-cyan-400 to-blue-600" },
  { id: "five-left", label: "5x", multiplier: 5, color: "from-emerald-400 to-teal-700" },
  { id: "ten-center", label: "10x", multiplier: 10, color: "from-amber-300 to-orange-600" },
  { id: "five-right", label: "5x", multiplier: 5, color: "from-emerald-400 to-teal-700" },
  { id: "two-right", label: "2x", multiplier: 2, color: "from-cyan-400 to-blue-600" },
  { id: "one-right", label: "1x", multiplier: 1, color: "from-sky-500 to-blue-700" },
  { id: "miss-right", label: "0x", multiplier: 0, color: "from-slate-500 to-slate-700" },
];

const DEFAULT_SETTINGS = {
  enabled: true,
  min_bet: 1,
  max_bet: 20,
  rules_text: "Choose a wallet bet, drop the ball, and the payout is bet x landed multiplier. Wallet updates after the live result.",
  slots: DEFAULT_SLOTS,
};

function money(value) {
  const amount = Number(value || 0);
  return `$${amount.toFixed(2)}`;
}

function resultText(multiplier) {
  const value = Number(multiplier || 0);
  if (value <= 0) return "Better luck";
  if (value < 2) return "Safe hit";
  if (value < 5) return "Nice drop";
  if (value < 10) return "Good win";
  return "Jackpot drop";
}

function pegRows() {
  const rows = [];
  for (let row = 0; row < 7; row += 1) {
    const count = row + 3;
    const y = 20 + row * 10.5;
    const start = 50 - (count - 1) * 5.7;
    for (let col = 0; col < count; col += 1) {
      rows.push({ x: start + col * 11.4, y, id: `${row}-${col}` });
    }
  }
  return rows;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function makePlinkoPath(targetIndex, slotCount) {
  const targetX = 8 + targetIndex * (84 / Math.max(1, slotCount - 1));
  const path = [{ x: 50, y: 7, tilt: 0 }];
  let x = 50;
  for (let row = 0; row < 7; row += 1) {
    const y = 18 + row * 9.7;
    const drift = (targetX - x) / Math.max(2, 8 - row);
    const bounceDirection = Math.random() > 0.5 ? 1 : -1;
    const bounce = bounceDirection * (3.8 + Math.random() * 4.8);
    x = clamp(x + drift + bounce, 10, 90);
    path.push({ x, y, tilt: bounceDirection * (18 + Math.random() * 26) });

    const rebound = -bounceDirection * (1.8 + Math.random() * 2.4);
    x = clamp(x + rebound, 10, 90);
    path.push({ x, y: y + 4.4, tilt: rebound * 7 });
  }
  path.push({ x: clamp((x + targetX) / 2, 8, 92), y: 78, tilt: targetX > x ? 24 : -24 });
  path.push({ x: targetX, y: 85, tilt: 0 });
  return path;
}

export default function PlinkoHomeGame({ dark = true, compact = false }) {
  const { user, refreshUser } = useAuth() || {};
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [walletBalance, setWalletBalance] = useState(null);
  const [betAmount, setBetAmount] = useState("1");
  const [dropping, setDropping] = useState(false);
  const [ball, setBall] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const timersRef = useRef([]);
  const pegs = useMemo(() => pegRows(), []);

  const slots = settings.slots?.length ? settings.slots : DEFAULT_SLOTS;
  const minBet = Number(settings.min_bet || 1);
  const maxBet = Number(settings.max_bet || minBet);
  const currentBet = Number(betAmount || 0);

  const loadPlinko = useCallback(async () => {
    if (!user || user.role !== "user") {
      setSettings(DEFAULT_SETTINGS);
      setWalletBalance(null);
      return;
    }
    try {
      const { data } = await api.get("/me/plinko");
      setSettings({ ...DEFAULT_SETTINGS, ...(data.settings || {}) });
      setWalletBalance(Number(data.wallet_balance || 0));
      const nextMin = Number(data.settings?.min_bet || DEFAULT_SETTINGS.min_bet);
      setBetAmount((value) => {
        const current = Number(value || 0);
        return current > 0 ? value : String(nextMin);
      });
    } catch {
      setSettings(DEFAULT_SETTINGS);
    }
  }, [user]);

  useEffect(() => {
    loadPlinko();
  }, [loadPlinko]);

  const clearDropTimers = useCallback(() => {
    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    timersRef.current = [];
  }, []);

  useEffect(() => () => clearDropTimers(), [clearDropTimers]);

  const dropBall = async () => {
    if (dropping) return;
    if (!user || user.role !== "user") {
      toast.error("Please sign in to play wallet Plinko.");
      return;
    }
    if (!settings.enabled) {
      toast.error("Plinko is turned off right now.");
      return;
    }
    if (!Number.isFinite(currentBet) || currentBet <= 0) {
      toast.error("Enter a valid bet amount.");
      return;
    }

    setDropping(true);
    setLastResult(null);
    clearDropTimers();
    try {
      const { data } = await api.post("/me/plinko/drop", { amount: currentBet });
      const result = data.result || {};
      const index = Number(result.index || 0);
      const slot = result.slot || slots[index] || slots[0];
      const path = makePlinkoPath(index, slots.length);

      setBall({
        key: Date.now(),
        ...path[0],
      });

      path.slice(1).forEach((point, stepIndex) => {
        const timer = window.setTimeout(() => {
          setBall((current) => current ? { ...current, ...point } : current);
        }, 80 + stepIndex * 115);
        timersRef.current.push(timer);
      });

      const finishTimer = window.setTimeout(async () => {
        const merged = {
          ...slot,
          ...result,
          index,
          text: resultText(slot.multiplier),
        };
        setLastResult(merged);
        setHistory((current) => [merged, ...current].slice(0, 4));
        setWalletBalance(Number(result.wallet_balance || 0));
        if (data.user) await refreshUser?.({ silent: true });
        setDropping(false);
        setBall(null);
        clearDropTimers();
      }, 80 + path.length * 115 + 420);
      timersRef.current.push(finishTimer);
    } catch (err) {
      toast.error(formatApiError(err, { audience: "player" }));
      setDropping(false);
      setBall(null);
      clearDropTimers();
    }
  };

  return (
    <section
      className={`relative overflow-hidden rounded-3xl border shadow-xl ${
        dark
          ? "border-[#2F6FA5]/25 bg-[#071421] text-white shadow-black/20"
          : "border-[#2F6FA5]/20 bg-white text-slate-950 shadow-slate-200/80"
      } ${compact ? "p-4 sm:p-5" : "p-5 sm:p-6 md:p-7"}`}
      data-testid="plinko-home-game"
    >
      <div className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-[#2F6FA5]/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-20 h-52 w-52 rounded-full bg-amber-400/15 blur-3xl" />

      <div className="relative grid gap-5 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <div className="order-2 flex flex-col lg:order-1">
          <div className="order-2 mb-2 mt-4 inline-flex items-center gap-2 self-start rounded-full border border-[#2F6FA5]/35 bg-[#2F6FA5]/15 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-[#8FC5F3] lg:order-none lg:mt-0">
            <Sparkles className="h-3.5 w-3.5" />
            Wallet Plinko
          </div>
          <h2 className="order-2 font-display text-3xl font-black tracking-tight sm:text-4xl lg:order-none">
            Plinko Drop
          </h2>
          <p className={`order-2 mt-2 max-w-xl text-sm leading-relaxed lg:order-none ${dark ? "text-slate-300" : "text-slate-600"}`}>
            {settings.rules_text || DEFAULT_SETTINGS.rules_text}
          </p>

          <div className="order-1 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end lg:order-none lg:mt-4">
            <label className="block">
              <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.18em] text-[#8FC5F3]">Bet amount</span>
              <input
                value={betAmount}
                onChange={(event) => setBetAmount(event.target.value)}
                type="number"
                min={minBet}
                max={maxBet}
                step="0.01"
                className={`w-full rounded-2xl border px-4 py-3 font-black outline-none ${
                  dark ? "border-white/10 bg-black/25 text-white" : "border-slate-200 bg-white text-slate-950"
                }`}
              />
            </label>
            <button
              type="button"
              onClick={dropBall}
              disabled={dropping || !settings.enabled}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2F6FA5] px-6 py-3 font-black text-white shadow-lg shadow-[#2F6FA5]/25 transition hover:-translate-y-0.5 hover:bg-[#4E91C8] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Zap className="h-4 w-4" />
              {dropping ? "Dropping..." : `Drop for ${money(currentBet || minBet)}`}
            </button>
          </div>

          <div className="order-1 mt-3 flex flex-wrap items-center gap-2 text-xs lg:order-none">
            <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 ${dark ? "border-white/10 bg-white/5 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
              <Wallet className="h-3.5 w-3.5" />
              Wallet: {walletBalance == null ? (user?.coins != null ? money(Number(user.coins || 0) / 1000) : "sign in") : money(walletBalance)}
            </span>
            <span className={`rounded-full border px-3 py-1 ${dark ? "border-white/10 bg-white/5 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
              Min {money(minBet)} / Max {money(maxBet)}
            </span>
          </div>

          {lastResult && (
            <div className={`order-1 mt-4 rounded-2xl border px-4 py-3 text-sm lg:order-none ${
              dark ? "border-white/10 bg-white/5" : "border-slate-200 bg-slate-50"
            }`}>
              <span className="text-slate-400">Result: </span>
              <span className="font-black text-[#2F6FA5]">{lastResult.text}</span>
              <span className="ml-2 font-black text-amber-400">{lastResult.slot?.label || lastResult.label}</span>
              <span className="ml-2 text-slate-400">Payout:</span>
              <span className="ml-1 font-black">{money(lastResult.payout_amount)}</span>
            </div>
          )}

          <div className="order-2 mt-5 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4 lg:order-none">
            {(history.length ? history : slots.slice(1, 5)).map((item, index) => (
              <div
                key={`${item.id || item.label}-${index}`}
                className={`rounded-xl border px-3 py-2 ${
                  dark ? "border-white/10 bg-white/5" : "border-slate-200 bg-slate-50"
                }`}
              >
                <div className="text-slate-400">{history.length ? "Recent" : "Possible"}</div>
                <div className="mt-0.5 font-black">{item.text || item.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className={`order-1 relative rounded-3xl border p-3 lg:order-2 ${
          dark ? "border-white/10 bg-[#030913]" : "border-slate-200 bg-[#071421]"
        }`}>
          <div className="relative mx-auto aspect-[1.05/1] max-w-[520px] overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#071421] via-[#0A2238] to-[#05070D]">
            <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
              <defs>
                <radialGradient id="plinkoPeg" cx="35%" cy="30%">
                  <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
                  <stop offset="55%" stopColor="#8FC5F3" stopOpacity="0.95" />
                  <stop offset="100%" stopColor="#2F6FA5" stopOpacity="0.95" />
                </radialGradient>
              </defs>
              <path d="M50 5 L55 13 H45 Z" fill="#F5B942" />
              {pegs.map((peg) => (
                <circle key={peg.id} cx={peg.x} cy={peg.y} r="1.55" fill="url(#plinkoPeg)" opacity="0.9" />
              ))}
              <path d="M7 88 C18 78, 82 78, 93 88" fill="none" stroke="#2F6FA5" strokeOpacity="0.35" strokeWidth="1" />
            </svg>

            {ball && (
              <div
                key={ball.key}
                className="absolute h-4 w-4 rounded-full bg-gradient-to-br from-white via-amber-200 to-amber-500 shadow-[0_0_24px_rgba(245,185,66,0.9)] transition-all duration-150 ease-out"
                style={{
                  left: `${ball.x}%`,
                  top: `${ball.y}%`,
                  transform: `translate(-50%, -50%) rotate(${Number(ball.tilt || 0)}deg)`,
                }}
              />
            )}

            <div className="absolute inset-x-3 bottom-3 grid gap-1" style={{ gridTemplateColumns: `repeat(${slots.length}, minmax(0, 1fr))` }}>
              {slots.map((slot, index) => (
                <div
                  key={`${slot.id || slot.label}-${index}`}
                  className={`rounded-lg bg-gradient-to-b ${slot.color || "from-slate-500 to-slate-700"} px-1 py-2 text-center text-[10px] font-black text-white shadow-inner sm:text-xs ${
                    lastResult?.index === index ? "ring-2 ring-white ring-offset-2 ring-offset-[#071421]" : ""
                  }`}
                >
                  {slot.label}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-center gap-2 text-xs font-bold text-slate-400">
            <CircleDollarSign className="h-4 w-4 text-[#2F6FA5]" />
            Uses wallet balance — every play is saved in wallet history
          </div>
        </div>
      </div>
    </section>
  );
}
